import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { zip } from 'fflate';
import {
  Upload,
  Download,
  X,
  Image as ImageIcon,
  Layers,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Trash2,
} from 'lucide-react';

// 3:4 oranı için hazır çıktı boyutları
const SIZE_PRESETS = [
  { value: '900x1200', w: 900, h: 1200, label: '900×1200 (küçük)' },
  { value: '1200x1600', w: 1200, h: 1600, label: '1200×1600 (orta)' },
  { value: '1500x2000', w: 1500, h: 2000, label: '1500×2000 (önerilen)' },
  { value: '1800x2400', w: 1800, h: 2400, label: '1800×2400 (büyük)' },
  { value: '2400x3200', w: 2400, h: 3200, label: '2400×3200 (XL)' },
];

const COLOR_PRESETS = [
  { name: 'Beyaz', hex: '#FFFFFF' },
  { name: 'Krem', hex: '#FAF7F2' },
  { name: 'Açık Bej', hex: '#F2EBE0' },
  { name: 'Pastel Pembe', hex: '#FDE7E5' },
  { name: 'Pastel Mavi', hex: '#E5F0FA' },
  { name: 'Açık Gri', hex: '#F2F2F2' },
  { name: 'Koyu Gri', hex: '#2A2A2A' },
  { name: 'Siyah', hex: '#000000' },
];

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve({ img, url });
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Görsel yüklenemedi')); };
    img.src = url;
  });
}

async function fitImageOnBackground(file, { canvasW, canvasH, bgColor, paddingPct, format = 'png', quality = 0.95 }) {
  const { img, url } = await loadImage(file);
  try {
    const canvas = document.createElement('canvas');
    canvas.width = canvasW;
    canvas.height = canvasH;
    const ctx = canvas.getContext('2d');

    // Arka plan dolgusu
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, canvasW, canvasH);

    // Padding hesabı: ürünün etrafında % oranında boşluk kalsın
    const pad = Math.max(0, Math.min(40, paddingPct)) / 100;
    const usableW = canvasW * (1 - pad * 2);
    const usableH = canvasH * (1 - pad * 2);

    const iw = img.naturalWidth || img.width;
    const ih = img.naturalHeight || img.height;
    const scale = Math.min(usableW / iw, usableH / ih);
    const dw = Math.round(iw * scale);
    const dh = Math.round(ih * scale);
    const dx = Math.round((canvasW - dw) / 2);
    const dy = Math.round((canvasH - dh) / 2);

    // PNG transparanlığı korunarak çizilir; renkli arka plan zaten dolduruldu
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, dx, dy, dw, dh);

    const mime = format === 'jpeg' ? 'image/jpeg' : 'image/png';
    const dataUrl = canvas.toDataURL(mime, quality);
    const blob = await new Promise((res) => canvas.toBlob((b) => res(b), mime, quality));
    return { dataUrl, blob, mime, width: canvasW, height: canvasH };
  } finally {
    URL.revokeObjectURL(url);
  }
}

function isHexColor(s) {
  return /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(s);
}

function bytesPretty(n) {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function PngBackgroundFitter() {
  const [items, setItems] = useState([]); // [{ id, file, originalUrl, originalW, originalH, processed: { dataUrl, blob, ... }, error, processing }]
  const [bgColor, setBgColor] = useState('#FFFFFF');
  const [hexInput, setHexInput] = useState('#FFFFFF');
  const [sizePreset, setSizePreset] = useState('1500x2000');
  const [customW, setCustomW] = useState(1500);
  const [customH, setCustomH] = useState(2000);
  const [paddingPct, setPaddingPct] = useState(8);
  const [format, setFormat] = useState('png');
  const [quality, setQuality] = useState(95);
  const [batchProcessing, setBatchProcessing] = useState(false);
  const [zipBuilding, setZipBuilding] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  const dragCounter = useRef(0);
  const [isDragging, setIsDragging] = useState(false);

  const sizeObj = useMemo(() => {
    if (sizePreset === 'custom') {
      const w = Math.max(50, Math.min(8000, Math.round(Number(customW) || 0)));
      const h = Math.max(50, Math.min(8000, Math.round(Number(customH) || 0)));
      return { value: 'custom', w, h, label: `${w}×${h} (özel)` };
    }
    return SIZE_PRESETS.find((s) => s.value === sizePreset) || SIZE_PRESETS[2];
  }, [sizePreset, customW, customH]);

  const settings = useMemo(() => ({
    canvasW: sizeObj.w,
    canvasH: sizeObj.h,
    bgColor,
    paddingPct,
    format,
    quality: quality / 100,
  }), [sizeObj, bgColor, paddingPct, format, quality]);

  // ── Files → items ───────────────────────────────────────────────────────────
  const addFiles = useCallback(async (filesList) => {
    const fileArr = Array.from(filesList || []);
    const validFiles = fileArr.filter((f) => /image\/png/i.test(f.type) || /\.png$/i.test(f.name));
    if (!validFiles.length) {
      setError('Sadece PNG dosyaları kabul edilir.');
      return;
    }
    setError('');
    const newItems = await Promise.all(validFiles.map(async (f) => {
      try {
        const { img, url } = await loadImage(f);
        return {
          id: `${f.name}_${f.size}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
          file: f,
          originalUrl: url,
          originalW: img.naturalWidth || img.width,
          originalH: img.naturalHeight || img.height,
          processed: null,
          error: null,
          processing: false,
        };
      } catch (e) {
        return {
          id: `${f.name}_${f.size}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
          file: f,
          originalUrl: '',
          originalW: 0,
          originalH: 0,
          processed: null,
          error: e?.message || 'Yüklenemedi',
          processing: false,
        };
      }
    }));
    setItems((prev) => [...prev, ...newItems]);
  }, []);

  // ── Tek bir item'ı işle ─────────────────────────────────────────────────────
  const processItem = useCallback(async (id, currentSettings) => {
    setItems((prev) => prev.map((it) => it.id === id ? { ...it, processing: true, error: null } : it));
    let outItem = null;
    try {
      const target = items.find((it) => it.id === id);
      if (!target) return null;
      const out = await fitImageOnBackground(target.file, currentSettings);
      outItem = out;
      setItems((prev) => prev.map((it) => it.id === id ? { ...it, processed: out, processing: false } : it));
    } catch (e) {
      setItems((prev) => prev.map((it) => it.id === id ? { ...it, error: e?.message || 'İşlenemedi', processing: false } : it));
    }
    return outItem;
  }, [items]);

  // ── Settings değişince tüm item'ları sırayla yeniden işle ───────────────────
  // (kullanıcı kontrolü için manuel "Hepsini Yeniden İşle" butonu var; otomatik yapmıyoruz)
  const processAll = useCallback(async () => {
    if (batchProcessing) return;
    if (!items.length) return;
    setBatchProcessing(true);
    try {
      // Sırayla işle (paralel canvas çoğaltımı tarayıcıyı boğabilir)
      for (const it of items) {
        if (it.error) continue;
        try {
          const out = await fitImageOnBackground(it.file, settings);
          setItems((prev) => prev.map((p) => p.id === it.id ? { ...p, processed: out, processing: false, error: null } : p));
        } catch (e) {
          setItems((prev) => prev.map((p) => p.id === it.id ? { ...p, error: e?.message || 'İşlenemedi', processing: false } : p));
        }
      }
    } finally {
      setBatchProcessing(false);
    }
  }, [batchProcessing, items, settings]);

  // İlk yüklemede otomatik işle
  useEffect(() => {
    const unprocessed = items.filter((it) => !it.processed && !it.error && !it.processing);
    if (unprocessed.length && !batchProcessing) {
      // Yeni eklenenleri sırayla işle
      (async () => {
        for (const it of unprocessed) {
          await processItem(it.id, settings);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  // ── Drag-drop handlers ──────────────────────────────────────────────────────
  const onFileInputChange = (e) => {
    const fs = e.target.files;
    if (fs && fs.length) addFiles(fs);
    e.target.value = '';
  };
  const onDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); dragCounter.current += 1; setIsDragging(true); };
  const onDragLeave = (e) => {
    e.preventDefault(); e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) { dragCounter.current = 0; setIsDragging(false); }
  };
  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    dragCounter.current = 0; setIsDragging(false);
    const fs = e.dataTransfer?.files;
    if (fs && fs.length) addFiles(fs);
  };

  // ── Color helpers ───────────────────────────────────────────────────────────
  const applyHex = (val) => {
    const v = val.startsWith('#') ? val : `#${val}`;
    setHexInput(v);
    if (isHexColor(v)) setBgColor(v);
  };

  // ── Download single ─────────────────────────────────────────────────────────
  const downloadOne = (it) => {
    if (!it.processed?.blob) return;
    const baseName = it.file.name.replace(/\.[a-z0-9]+$/i, '');
    const ext = it.processed.mime === 'image/jpeg' ? 'jpg' : 'png';
    const url = URL.createObjectURL(it.processed.blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${baseName}_${sizeObj.w}x${sizeObj.h}.${ext}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Download all as ZIP ─────────────────────────────────────────────────────
  const downloadAllZip = async () => {
    if (zipBuilding) return;
    const ready = items.filter((it) => it.processed?.blob);
    if (!ready.length) {
      setError('İndirilecek işlenmiş görsel yok.');
      return;
    }
    setError('');
    setZipBuilding(true);
    try {
      const fileMap = {};
      const used = {};
      for (const it of ready) {
        const baseName = it.file.name.replace(/\.[a-z0-9]+$/i, '');
        const ext = it.processed.mime === 'image/jpeg' ? 'jpg' : 'png';
        let candidate = `${baseName}_${sizeObj.w}x${sizeObj.h}.${ext}`;
        let n = 1;
        while (used[candidate]) {
          candidate = `${baseName}_${sizeObj.w}x${sizeObj.h}_${n}.${ext}`;
          n += 1;
        }
        used[candidate] = true;
        const buf = new Uint8Array(await it.processed.blob.arrayBuffer());
        fileMap[candidate] = buf;
      }
      const zipped = await new Promise((resolve, reject) => {
        zip(fileMap, { level: 6 }, (err, data) => {
          if (err) reject(err); else resolve(data);
        });
      });
      const blob = new Blob([zipped], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const ts = new Date().toISOString().slice(0, 16).replace(/[T:]/g, '-');
      a.href = url;
      a.download = `bg-fitter_${sizeObj.w}x${sizeObj.h}_${ts}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.message || 'ZIP oluşturulamadı.');
    } finally {
      setZipBuilding(false);
    }
  };

  const removeItem = (id) => {
    setItems((prev) => {
      const it = prev.find((x) => x.id === id);
      if (it?.originalUrl) URL.revokeObjectURL(it.originalUrl);
      return prev.filter((x) => x.id !== id);
    });
  };

  const clearAll = () => {
    items.forEach((it) => { if (it.originalUrl) URL.revokeObjectURL(it.originalUrl); });
    setItems([]);
    setError('');
  };

  const stats = useMemo(() => ({
    total: items.length,
    ready: items.filter((it) => it.processed).length,
    errored: items.filter((it) => it.error).length,
    processing: items.filter((it) => it.processing).length,
  }), [items]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-50 text-sky-700 text-xs font-semibold ring-1 ring-sky-200 mb-3">
            <Layers className="w-3.5 h-3.5" />
            Toplu Görsel Hazırlayıcı
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">PNG → Renkli Arka Plan + Toplu Çıktı</h1>
          <p className="mt-2 text-gray-600 text-sm max-w-2xl leading-relaxed">
            Şeffaf PNG'leri yükle, arka plan rengini seç, çıktı boyutunu belirle (3:4 hazır boyutlar veya özel ölçü).
            Tüm görseller ortalanmış, kenar boşluklu ve seçtiğin renkli zeminle hazırlanır. Toplu işlem ve ZIP indirme destekli.
          </p>
        </div>

        {/* Settings */}
        <div className="bg-white rounded-2xl ring-1 ring-gray-200 shadow-sm p-5 mb-5">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            {/* Background Color */}
            <div className="md:col-span-5">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Arka Plan Rengi</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={bgColor}
                  onChange={(e) => { setBgColor(e.target.value); setHexInput(e.target.value.toUpperCase()); }}
                  className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer"
                />
                <input
                  type="text"
                  value={hexInput}
                  onChange={(e) => applyHex(e.target.value.toUpperCase())}
                  placeholder="#FFFFFF"
                  className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-sky-200 font-mono"
                />
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {COLOR_PRESETS.map((c) => (
                  <button
                    key={c.hex}
                    onClick={() => { setBgColor(c.hex); setHexInput(c.hex); }}
                    title={`${c.name} ${c.hex}`}
                    style={{ backgroundColor: c.hex }}
                    className={`w-7 h-7 rounded-full border-2 transition-all ${bgColor.toUpperCase() === c.hex.toUpperCase() ? 'border-sky-500 ring-2 ring-sky-200' : 'border-gray-200 hover:border-gray-400'}`}
                  />
                ))}
              </div>
            </div>

            {/* Size */}
            <div className="md:col-span-3">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Çıktı Boyutu</label>
              <select
                value={sizePreset}
                onChange={(e) => setSizePreset(e.target.value)}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-sky-200"
              >
                <optgroup label="3:4 hazır boyutlar">
                  {SIZE_PRESETS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </optgroup>
                <option value="custom">Özel boyut…</option>
              </select>
              {sizePreset === 'custom' && (
                <div className="mt-2 flex items-center gap-1.5">
                  <input
                    type="number"
                    min="50"
                    max="8000"
                    step="1"
                    value={customW}
                    onChange={(e) => setCustomW(e.target.value === '' ? '' : Number(e.target.value))}
                    placeholder="G"
                    className="w-full text-sm border border-gray-200 rounded-lg px-2 py-2 outline-none focus:ring-2 focus:ring-sky-200 font-mono"
                  />
                  <span className="text-gray-400 text-sm font-medium">×</span>
                  <input
                    type="number"
                    min="50"
                    max="8000"
                    step="1"
                    value={customH}
                    onChange={(e) => setCustomH(e.target.value === '' ? '' : Number(e.target.value))}
                    placeholder="Y"
                    className="w-full text-sm border border-gray-200 rounded-lg px-2 py-2 outline-none focus:ring-2 focus:ring-sky-200 font-mono"
                  />
                  <span className="text-[10px] text-gray-400">px</span>
                </div>
              )}
            </div>

            {/* Padding */}
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Kenar Boşluğu</label>
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  min="0"
                  max="25"
                  value={paddingPct}
                  onChange={(e) => setPaddingPct(Number(e.target.value))}
                  className="flex-1"
                />
                <span className="text-xs font-medium text-gray-700 w-10 text-right">%{paddingPct}</span>
              </div>
            </div>

            {/* Format */}
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Format</label>
              <div className="flex gap-1">
                <button
                  onClick={() => setFormat('png')}
                  className={`flex-1 text-xs font-medium px-2 py-2 rounded-lg ${format === 'png' ? 'bg-sky-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >PNG</button>
                <button
                  onClick={() => setFormat('jpeg')}
                  className={`flex-1 text-xs font-medium px-2 py-2 rounded-lg ${format === 'jpeg' ? 'bg-sky-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >JPEG</button>
              </div>
              {format === 'jpeg' && (
                <div className="mt-1 flex items-center gap-1">
                  <input type="range" min="60" max="100" value={quality} onChange={(e) => setQuality(Number(e.target.value))} className="flex-1" />
                  <span className="text-[10px] text-gray-500 w-7 text-right">{quality}</span>
                </div>
              )}
            </div>
          </div>

          {/* Apply / Clear actions */}
          <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap items-center gap-2">
            <button
              onClick={processAll}
              disabled={batchProcessing || !items.length}
              className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 disabled:bg-gray-300 text-white text-sm font-semibold px-4 py-2 rounded-lg shadow-sm"
            >
              {batchProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Layers className="w-4 h-4" />}
              {batchProcessing ? 'İşleniyor…' : 'Hepsini Yeniden İşle'}
            </button>
            <button
              onClick={downloadAllZip}
              disabled={zipBuilding || !stats.ready}
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white text-sm font-semibold px-4 py-2 rounded-lg shadow-sm"
            >
              {zipBuilding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {zipBuilding ? 'ZIP hazırlanıyor…' : `Tümünü ZIP İndir (${stats.ready})`}
            </button>
            {items.length > 0 && (
              <button
                onClick={clearAll}
                className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-red-600 ml-auto"
              >
                <Trash2 className="w-3.5 h-3.5" /> Tümünü temizle
              </button>
            )}
          </div>
        </div>

        {/* Drop zone */}
        <div
          onDragEnter={onDragEnter}
          onDragLeave={onDragLeave}
          onDragOver={onDragOver}
          onDrop={onDrop}
          className={`bg-white rounded-2xl ring-1 ring-gray-200 shadow-sm p-8 mb-5 text-center cursor-pointer transition-all ${isDragging ? 'ring-2 ring-sky-400 bg-sky-50' : 'hover:bg-gray-50'}`}
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="w-10 h-10 mx-auto text-gray-400 mb-2" />
          <p className="text-sm font-medium text-gray-700">PNG dosyalarını buraya sürükle bırak</p>
          <p className="text-xs text-gray-500 mt-1">veya tıkla, birden fazla dosya seçebilirsin</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png"
            multiple
            onChange={onFileInputChange}
            className="hidden"
          />
        </div>

        {/* Stats */}
        {items.length > 0 && (
          <div className="flex items-center gap-3 text-xs text-gray-600 mb-4">
            <span><strong>{stats.total}</strong> görsel</span>
            <span className="text-gray-400">·</span>
            <span className="text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5 inline mr-1" />{stats.ready} hazır</span>
            {stats.processing > 0 && (
              <>
                <span className="text-gray-400">·</span>
                <span className="text-sky-700"><Loader2 className="w-3.5 h-3.5 inline mr-1 animate-spin" />{stats.processing} işleniyor</span>
              </>
            )}
            {stats.errored > 0 && (
              <>
                <span className="text-gray-400">·</span>
                <span className="text-red-700"><AlertCircle className="w-3.5 h-3.5 inline mr-1" />{stats.errored} hatalı</span>
              </>
            )}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mb-5 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Grid of items */}
        {items.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {items.map((it) => (
              <div key={it.id} className="bg-white rounded-2xl ring-1 ring-gray-200 shadow-sm overflow-hidden">
                <div
                  className="relative bg-gray-50 flex items-center justify-center"
                  style={{ aspectRatio: `${sizeObj.w} / ${sizeObj.h}` }}
                >
                  {it.processing && (
                    <div className="flex flex-col items-center gap-1 text-gray-500">
                      <Loader2 className="w-5 h-5 animate-spin text-sky-500" />
                      <p className="text-[10px]">İşleniyor…</p>
                    </div>
                  )}
                  {!it.processing && it.processed?.dataUrl && (
                    <img src={it.processed.dataUrl} alt={it.file.name} className="w-full h-full object-contain" />
                  )}
                  {!it.processing && !it.processed?.dataUrl && it.error && (
                    <div className="flex flex-col items-center gap-1 text-red-600 px-2 text-center">
                      <AlertCircle className="w-5 h-5" />
                      <p className="text-[10px]">{it.error}</p>
                    </div>
                  )}
                  {!it.processing && !it.processed?.dataUrl && !it.error && (
                    <ImageIcon className="w-8 h-8 text-gray-300" />
                  )}
                  <button
                    onClick={() => removeItem(it.id)}
                    className="absolute top-2 right-2 w-6 h-6 rounded-full bg-white/90 ring-1 ring-gray-200 hover:bg-red-50 hover:ring-red-200 flex items-center justify-center"
                    title="Bu görseli kaldır"
                  >
                    <X className="w-3.5 h-3.5 text-gray-600" />
                  </button>
                </div>
                <div className="px-3 py-2 border-t border-gray-100">
                  <p className="text-[11px] font-medium text-gray-700 truncate" title={it.file.name}>{it.file.name}</p>
                  <p className="text-[10px] text-gray-400">
                    Orijinal: {it.originalW}×{it.originalH} · {bytesPretty(it.file.size)}
                  </p>
                </div>
                <div className="px-3 pb-3">
                  <button
                    onClick={() => downloadOne(it)}
                    disabled={!it.processed}
                    className="w-full inline-flex items-center justify-center gap-1 text-[11px] font-semibold bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white px-2 py-1.5 rounded-lg"
                  >
                    <Download className="w-3 h-3" /> İndir
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
